"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { InfoHint } from "./InfoHint";

export interface NavTabItem {
  href: string;
  label: string;
  hint: string;
}

/**
 * Section nav with active-tab highlighting + per-tab info hints. Client component so it can
 * read the current pathname. `rootHref` is the section index (e.g. "/compass" or
 * "/compass/anchor") — it matches only exactly, so it isn't lit up by deeper sub-routes.
 */
export function NavTabs({
  items,
  rootHref,
  ariaLabel,
  size = "md",
}: {
  items: NavTabItem[];
  rootHref: string;
  ariaLabel: string;
  size?: "md" | "sm";
}) {
  const pathname = usePathname();
  const linkPad = size === "sm" ? "pl-3 pr-1 py-1.5" : "pl-4 pr-1.5 py-2";
  const iconMargin = size === "sm" ? "mr-2" : "mr-2.5";

  const isActive = (href: string) =>
    pathname === href || (href !== rootHref && pathname.startsWith(href + "/"));

  return (
    <nav className="flex flex-wrap gap-1 border-b border-surface-light/30 mb-6" aria-label={ariaLabel}>
      {items.map((item) => {
        const active = isActive(item.href);
        return (
          <span key={item.href} className="inline-flex items-center">
            <Link
              href={item.href}
              aria-current={active ? "page" : undefined}
              className={`${linkPad} text-sm transition-colors border-b-2 -mb-px ${
                active
                  ? "text-foreground border-blue font-medium"
                  : "text-muted-foreground hover:text-foreground border-transparent hover:border-blue/40"
              }`}
            >
              {item.label}
            </Link>
            <InfoHint label={item.label} text={item.hint} className={iconMargin} />
          </span>
        );
      })}
    </nav>
  );
}
